// @flow

//
// !!! IMPORTANT !!!
//
// This component is deprecated, use Link from @zapier/design-system instead
//

import * as React from 'react';
import bemify, { toModifiers } from 'bemify-js';
import { noop } from 'lodash';

import { createKeyHandler } from '@zapier/common-utils';
import { SSRLink, isMatchingRoute } from '@zapier/ssr-browser';

import 'web-ssr/app/common/components/Link.scss';

import type { Node } from 'react';

import type { DisplayTarget, KindType } from 'web-ssr/app/common/types';

const bem = bemify('link');

type BaseProps = {
  children: Node,
  target?: DisplayTarget,
  className?: string,
  itemprop?: string,
  kind?: KindType,
  rel?: string,
  onClick?: (event: SyntheticEvent<>) => any,
};

type RouterLinkProps = {
  ...$Exact<BaseProps>,
  as?: string | {},
  href?: string | {},
  params?: {},
  prefetch: boolean,
  replace: boolean,
  role?: string,
  scroll: boolean,
  shallow: boolean,
  title?: string,
};

const Anchor = React.forwardRef<RouterLinkProps, HTMLAnchorElement>(
  (props, ref) => {
    const {
      children,
      className,
      href,
      itemprop,
      kind,
      onClick = noop,
      target,
      title,
      rel,
      role,
    } = props;

    return (
      <a
        className={bem(className, toModifiers(kind))}
        href={href}
        itemProp={itemprop}
        onClick={onClick}
        onKeyDown={createKeyHandler(['enter'], onClick)}
        ref={ref}
        rel={rel}
        role={role}
        tabIndex="0"
        target={target}
        title={title}
      >
        {children}
      </a>
    );
  }
);

const NextWrappedLink = (props: RouterLinkProps) => {
  return (
    <SSRLink
      as={props.as}
      href={props.href}
      prefetch={props.prefetch}
      replace={props.replace}
      scroll={props.scroll}
      shallow={props.shallow}
      style={{ textDecoration: 'none' }}
    >
      <Anchor {...props}>{props.children}</Anchor>
    </SSRLink>
  );
};

const Link = (props: RouterLinkProps) => {
  // Determines if this is a Next.js hosted link, if it is we wrap it in
  // the Next.js Link wrapper. Otherwise we return a vanilla Anchor link.
  if (props.href && isMatchingRoute(props.href)) {
    return <NextWrappedLink {...props}>{props.children}</NextWrappedLink>;
  }

  return <Anchor {...props}>{props.children}</Anchor>;
};

Link.defaultProps = {
  prefetch: false,
  replace: false,
  role: 'button',
  shallow: false,
  scroll: false,
};

export default Link;
